// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import closeQuickViewModal from "@bg/App/Handlers/ProductQuickView/CloseQuickViewModal";
import setQuickViewProduct from "@bg/App/Handlers/ProductQuickView/SetQuickViewProduct";
import setSelectedImageIndex from "@bg/App/Handlers/ProductQuickView/SetSelectedImageIndex";
import updateQuickViewProduct from "@bg/App/Handlers/ProductQuickView/UpdateQuickViewProduct";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { getQuickViewProductState, getSelectedImage } from "@bg/App/Store/Selectors/ProductQuickView/QuickViewSelector";
import { ProductDetailBrandComponent } from "@bg/App/Widgets/ProductDetails/ProductDetailsBrand";
import { ProductDetailsQrCodeComponent } from "@bg/App/Widgets/ProductDetails/ProductDetailsQrCode";
import { ProductDetailUPCComponent } from "@bg/App/Widgets/ProductDetails/ProductDetailsUpc";
import LazyImage from "@bg/MobiusOverrides/LazyImage";
import { ProductAvailabilityStyles, productAvailabilityStyles } from "@bg/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@bg/Overrides/Components/ProductContextAvailability";
import { ProductPriceStyles, productPriceStyles } from "@bg/Overrides/Components/ProductPrice";
import ProductPrimaryImage, { ProductPrimaryImageStyles } from "@bg/Overrides/Components/ProductPrimaryImage";
import { CustomerPartNumberComponent } from "@bg/Overrides/Widgets/ProductDetails/ProductDetailsCustomerPartNumber";
import { ProductDetailsDescriptionComnponent } from "@bg/Overrides/Widgets/ProductDetails/ProductDetailsDescription";
import { MfrPartNumberComponent } from "@bg/Overrides/Widgets/ProductDetails/ProductDetailsManufacturingPartNumber";
import { PartNumberComponent } from "@bg/Overrides/Widgets/ProductDetails/ProductDetailsPartNumber";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { ImageModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink from "@insite/content-library/Components/ProductAddToListLink";
import ProductImageCarousel from "@insite/content-library/Components/ProductImageCarousel";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    skipInventoryValidation?: boolean;
    extendedStyles?: ProductQuickViewModalStyles;
}

const mapStateToProps = (state: CustomAppState) => {
    const { isLoading, isModalOpen, product, productInfo, selectedImage } = getQuickViewProductState(state);
    return {
        isLoading,
        isModalOpen,
        product,
        productInfo,
        selectedImage,
        selectedImageIndex: getSelectedImage(state, product),
    };
};

const mapDispatchToProps = {
    closeQuickViewModal,
    setQuickViewProduct,
    updateQuickViewProduct,
    setSelectedImageIndex,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface ProductQuickViewModalStyles {
    modal?: ModalPresentationProps;
    titleText?: LinkPresentationProps;
    productImagoryAndDetails?: InjectableCss;
    imagoryWrapper?: InjectableCss;
    imageCarousel?: InjectableCss;
    primaryImageWrapper?: InjectableCss;
    primaryImage?: ProductPrimaryImageStyles;
    detailsWrapper?: InjectableCss;
    productNumbersWrapper?: InjectableCss;
    priceInventoryQrCode?: InjectableCss;
    priceWrapper?: InjectableCss;
    inventoryWrapper?: InjectableCss;
    qrCode?: InjectableCss;
    productActions?: InjectableCss;
    qtyOrdredWrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addTolistWrapper?: InjectableCss;
    viewFulDetailsLink?: LinkPresentationProps;
    productPrice?: ProductPriceStyles;
    productavailability?: ProductAvailabilityStyles;
}

export const quickViewModalStyles: ProductQuickViewModalStyles = {
    titleText: {
        typographyProps: {
            variant: "h2",
            forwardAs: "span",
        },
        css: css`
            color: ${getColor("secondary.main")} !important;
            width: fit-content;
        `,
    },
    productImagoryAndDetails: {
        css: css`
            margin-bottom: 50px;
            display: flex;
            flex-wrap: wrap;
        `,
    },
    imagoryWrapper: {
        css: css`
            width: 550px;
            display: flex;
            justify-content: space-between;
            @media (max-width: 1200px) {
                width: 500px;
            }
        `,
    },
    imageCarousel: {
        css: css`
            width: 88px;
            padding-top: 25px;
        `,
    },
    primaryImageWrapper: {
        css: css`
            width: calc(100% - 115px);
            margin-left: 27px;
        `,
    },
    primaryImage: {
        image: {
            width: "100%",
            cloudinaryWidth: "366",
            cloudinary: true,
            css: css`
                &&:hover {
                    cursor: default;
                }
                img {
                    max-width: 100%;
                    max-height: 366px;
                }
            `,
        },
    },
    detailsWrapper: {
        css: css`
            width: calc(100% - 580px);
            margin-left: 30px;
            display: flex;
            flex-direction: column;
            @media (max-width: 1200px) {
                width: calc(100% - 520px);
                margin-left: 20px;
            }
        `,
    },
    productNumbersWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            padding-bottom: 20px;
            margin-bottom: 17px;
            border-bottom: 1px solid ${getColor("common.ui2")};
            && div[class*="Wrapper-sc"] {
                width: auto;
            }
        `,
    },
    priceInventoryQrCode: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            margin-bottom: 15px;
        `,
    },
    priceWrapper: {
        css: css`
            margin-right: 15px;
        `,
    },
    qrCode: {
        css: css`
            flex: 1;
            display: flex;
            justify-content: flex-end;
        `,
    },
    productActions: {
        css: css`
            display: flex;
        `,
    },
    qtyOrdredWrapper: {
        css: css`
            max-width: 130px;
            margin-right: 10px;
        `,
    },
    addToCartWrapper: {
        css: css`
            margin-right: 23px;
        `,
    },
    addTolistWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    viewFulDetailsLink: {
        color: "primary",
    },
    productPrice: {
        price: {
            ...productPriceStyles.price,
            priceText: {
                weight: fontConfig.weight.bold,
                size: 18,
            },
            nonStockText: {
                css: css`
                    ${productPriceStyles.price?.nonStockText?.css}
                    max-width: 400px;
                `,
            },
        },
    },
    productavailability: {
        messageText: {
            size: fontConfig.fontSize.small,
        },
        nonStockText: {
            css: css`
                ${productAvailabilityStyles.nonStockText?.css}
                max-width: 400px;
            `,
        },
    },
};

const ProductQuickViewtModal: React.FC<Props> = ({
    extendedStyles,
    isModalOpen,
    product,
    productInfo,
    closeQuickViewModal,
    updateQuickViewProduct,
    setSelectedImageIndex,
    selectedImageIndex,
    selectedImage,
    history,
}) => {
    const [styles] = useState(() => mergeToNew(quickViewModalStyles, extendedStyles));

    const closeModalHandler = () => {
        closeQuickViewModal();
    };

    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    const onQtyChangeHandler = (qtyOrdered: number) => {
        updateQuickViewProduct({ qtyOrdered, isQty: true });
    };

    const onUnitOfMeasureChanged = (unitOfMeasure: string) => {
        updateQuickViewProduct({ unitOfMeasure });
    };

    const productContext: ProductContextModel = {
        product,
        productInfo,
        onQtyOrderedChanged: onQtyChangeHandler,
        onUnitOfMeasureChanged,
    };

    const redirectToPdp = (url: string) => {
        closeQuickViewModal({
            onComplete: () => {
                if (url) {
                    history.push(url);
                }
            },
        });
    };

    const image =
        selectedImageIndex ??
        ({
            id: product.id,
            imageAltText: product.imageAltText,
            imageType: "Static",
            largeImagePath: product.largeImagePath,
            mediumImagePath: product.mediumImagePath,
            smallImagePath: product.smallImagePath,
        } as ImageModel);

    const path = image.mediumImagePath || image.largeImagePath;

    const displayBadge = product?.properties?.badgeIndicatorIcon;

    return (
        <Modal size={1440} isOpen={isModalOpen} handleClose={closeModalHandler}>
            <ProductContext.Provider value={productContext}>
                <StyledWrapper {...styles.productImagoryAndDetails}>
                    <StyledWrapper {...styles.imagoryWrapper}>
                        {product.images && product.images.length > 1 && (
                            <StyledWrapper {...styles.imageCarousel}>
                                <ProductImageCarousel
                                    selectedIndex={selectedImage}
                                    onSelectImage={selectImageHandler}
                                />
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.primaryImageWrapper}>
                            {path ? (
                                <ProductPrimaryImage
                                    product={product}
                                    image={image}
                                    extendedStyles={styles.primaryImage}
                                />
                            ) : (
                                <LazyImage
                                    src=""
                                    cloudinaryWidth="366"
                                    altText={image.imageAltText}
                                    data-test-selector="productDetails_mainImage"
                                />
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.detailsWrapper}>
                        {product.productTitle && (
                            <Link
                                onClick={() => redirectToPdp(product.canonicalUrl)}
                                {...styles.titleText}
                                data-test-selector="ProductQuickViewPageTitle"
                            >
                                {parse(product.productTitle)}
                            </Link>
                        )}
                        <StyledWrapper {...styles.productNumbersWrapper}>
                            <MfrPartNumberComponent product={product} productInfo={productInfo} />
                            <PartNumberComponent product={product} productInfo={productInfo} />
                            <CustomerPartNumberComponent product={product} productInfo={productInfo} />
                            <ProductDetailBrandComponent product={product} productInfo={productInfo} />
                            <ProductDetailUPCComponent product={product} productInfo={productInfo} />
                        </StyledWrapper>

                        <ProductDetailsDescriptionComnponent product={product} productInfo={productInfo} />

                        <StyledWrapper {...styles.priceInventoryQrCode}>
                            <StyledWrapper {...styles.priceWrapper}>
                                <ProductPrice
                                    showUnitOfMeasure={true}
                                    showLabel={false}
                                    product={productContext}
                                    extendedStyles={styles.productPrice as any}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.inventoryWrapper}>
                                <ProductContextAvailability extendedStyles={styles.productavailability as any} />
                            </StyledWrapper>
                            <StyledWrapper {...styles.qrCode}>
                                <ProductDetailsQrCodeComponent productContext={productContext} />
                            </StyledWrapper>
                        </StyledWrapper>

                        <StyledWrapper {...styles.productActions}>
                            <StyledWrapper {...styles.qtyOrdredWrapper}>
                                <ProductQuantityOrdered />
                            </StyledWrapper>
                            <StyledWrapper {...styles.addToCartWrapper}>
                                <ProductAddToCartButton
                                    {...({ iconOnly: false } as any)}
                                    data-test-selector={`product_quickView_${product.id}`}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.addTolistWrapper}>
                                <ProductAddToListLink />
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <Link onClick={() => redirectToPdp(product.canonicalUrl)} {...styles.viewFulDetailsLink}>
                    {translate("View Full Product Details")}
                </Link>
            </ProductContext.Provider>
        </Modal>
    );
};

export default withHistory(connect(mapStateToProps, mapDispatchToProps)(ProductQuickViewtModal));
